'use client';

export const dynamic = 'force-dynamic';

import { useState, useEffect, useRef, Suspense } from 'react';
import Link from 'next/link';
import { useSearchParams } from 'next/navigation';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardFooter } from "@/components/ui/card";
import { useAuth } from '@/lib/auth/auth-context';
import Image from "next/image";
import { ButtonSpinner } from '@/components/ui/spinner';
import { Eye, EyeOff, Mail, Lock, Shield, ArrowRight } from 'lucide-react';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import dynamicImport from 'next/dynamic';
import gsap from 'gsap';
import { TurnstileWidget, isCaptchaConfigured, type TurnstileInstance } from '@/components/TurnstileWidget';

// Dynamically import URLErrorHandler with no SSR to avoid hydration issues
const URLErrorHandler = dynamicImport(
  () => import('@/components/ui/SimpleLoadingError').then(mod => mod.URLErrorHandler),
  { ssr: false }
);

function LoginForm() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const captchaRef = useRef<TurnstileInstance>(null);
  const [showLoadingScreen, setShowLoadingScreen] = useState(false);
  const { signIn, isLoading } = useAuth();
  const loginTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const MAX_LOGIN_TIME = 10000; // 10 seconds timeout
  const searchParams = useSearchParams();
  
  // Animation refs
  const logoRef = useRef<HTMLDivElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const footerRef = useRef<HTMLDivElement>(null);
  const loadingScreenRef = useRef<HTMLDivElement>(null);
  
  // Handle URL parameters from signup redirect
  useEffect(() => {
    const emailParam = searchParams?.get('email');
    const messageParam = searchParams?.get('message');
    
    if (emailParam) {
      setEmail(emailParam);
    }
    
    if (messageParam) {
      // Show message from signup redirect
      setTimeout(() => {
        toast.info(messageParam);
      }, 500);
    }
  }, [searchParams]);

  // Clear timeout on component unmount
  useEffect(() => {
    return () => {
      if (loginTimeoutRef.current) {
        clearTimeout(loginTimeoutRef.current);
      }
    };
  }, []);

  // Animation on mount
  useEffect(() => {
    // Set initial states
    if (logoRef.current) {
      gsap.set(logoRef.current, {
        y: -50,
        opacity: 0
      });
    }
    
    if (cardRef.current) {
      gsap.set(cardRef.current, {
        y: -50,
        opacity: 0
      });
    }
    
    if (footerRef.current) {
      gsap.set(footerRef.current, {
        y: 30,
        opacity: 0
      });
    }

    // Animate elements with staggered delays
    const tl = gsap.timeline();
    
    if (logoRef.current) {
      tl.to(logoRef.current, {
        y: 0,
        opacity: 1,
        duration: 0.8,
        ease: "power2.out"
      });
    }
    
    if (cardRef.current) {
      tl.to(cardRef.current, {
        y: 0,
        opacity: 1,
        scale: 1,
        duration: 1,
        ease: "power2.out"
      }, "-=0.4");
    }
    
    if (footerRef.current) {
      tl.to(footerRef.current, {
        y: 0,
        opacity: 1,
        duration: 0.6,
        ease: "power2.out"
      }, "-=0.3");
    }
  }, []);

  const handleStuckLogin = () => {
    // Clear all cookies
    document.cookie.split(';').forEach(cookie => {
      const [name] = cookie.trim().split('=');
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
    });
    
    // Force reload the page
    window.location.href = '/login';
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    
    // Show loading screen immediately
    setShowLoadingScreen(true);
    
    // Animate loading screen entrance
    if (loadingScreenRef.current) {
      gsap.set(loadingScreenRef.current, {
        opacity: 0
      });
      
      gsap.to(loadingScreenRef.current, {
        opacity: 1,
        duration: 0.3,
        ease: "power2.out"
      });
    }
    
    // Set timeout to detect stuck login
    loginTimeoutRef.current = setTimeout(() => {
      handleStuckLogin();
    }, MAX_LOGIN_TIME);
    
    try {
      await signIn(email, password, captchaToken ?? undefined);
      // Clear the timeout if login succeeds
      if (loginTimeoutRef.current) {
        clearTimeout(loginTimeoutRef.current);
      }
    } catch (error) {
      // Hide loading screen on error
      setShowLoadingScreen(false);
      // Clear the timeout if login fails
      if (loginTimeoutRef.current) {
        clearTimeout(loginTimeoutRef.current);
      }
      // Turnstile tokens are single-use — get a fresh one for the retry
      setCaptchaToken(null);
      captchaRef.current?.reset();
      setError(error instanceof Error ? error.message : 'Failed to sign in');
    }
  };
  
  return (
    <div className="min-h-screen relative">
      {/* Loading Screen */}
      {showLoadingScreen && (
        <div 
          ref={loadingScreenRef}
          className="fixed inset-0 z-50 bg-green-600 flex items-center justify-center"
        >
          <div className="text-center">
            <div className="mb-8">
              <Image 
                src="https://rgvfkcfnfbtqyjlrvynz.supabase.co/storage/v1/object/public/vendor-assets/vendor-assets/logo-white.png" 
                alt="MenuMate Logo" 
                width={200} 
                height={80} 
                className="h-auto w-auto mx-auto animate-pulse"
                priority 
              />
            </div>
            <div className="flex items-center justify-center space-x-2">
              <div className="w-2 h-2 bg-white rounded-full animate-bounce"></div>
              <div className="w-2 h-2 bg-white rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
              <div className="w-2 h-2 bg-white rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
            </div>
            <p className="text-white mt-4 text-lg font-medium">Logging you in...</p>
          </div>
        </div>
      )}

      {/* Full-screen Background Image */}
      <div className="absolute inset-0">
        <Image
          src="https://rgvfkcfnfbtqyjlrvynz.supabase.co/storage/v1/object/public/vendor-assets/vendor-assets/bg-1.png"
          alt="Login background"
          width={1920}
          height={1080}
          className="h-full w-full object-cover"
          priority
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-black" />
      </div>

      {/* Centered Content */}
      <div className="relative z-10 min-h-screen flex items-center justify-center p-6">
        <div className="w-full max-w-md space-y-6">
          {/* Logo */}
          <div ref={logoRef} className="flex justify-center">
            <Link href="/" className="flex items-center gap-2 font-medium">
              <Image 
                src="https://rgvfkcfnfbtqyjlrvynz.supabase.co/storage/v1/object/public/vendor-assets/vendor-assets/white-full.png" 
                alt="MenuMate Logo" 
                width={150} 
                height={50} 
                className="h-auto w-auto"
                priority 
              />
            </Link>
          </div>

          {/* Form Card */}
          <Card ref={cardRef} className="rounded-2xl shadow-2xl">
            <CardContent className="px-9 pb-9">
              
              {/* Header */}
              <div className="space-y-2 text-left py-6 mb-4">
                <h1 className="text-2xl font-semibold text-black">Welcome back</h1>
                <p className="text-black/60">
                  Login to your MenuMate account
                </p>
              </div>

              <Separator className='mb-8' />
              
              <URLErrorHandler />
              
              <form onSubmit={handleSubmit} className="space-y-4">
                {error && (
                  <div className="bg-destructive/15 text-destructive p-3 rounded-md text-sm">
                    {error}
                  </div>
                )}
                
                <div className="space-y-2">
                  <Label htmlFor="email" className="flex items-center gap-2">
                    <Mail className="w-4 h-4" />
                    Email
                  </Label>
                  <Input 
                    id="email"
                    type="email" 
                    placeholder="your@email.com" 
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    autoComplete="username"
                    required
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="password" className="flex items-center gap-2">
                    <Lock className="w-4 h-4" />
                    Password
                  </Label>
                  <div className="relative">
                    <Input 
                      id="password"
                      type={showPassword ? "text" : "password"}
                      placeholder="Enter your password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      autoComplete="current-password"
                      className="pr-10"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none"
                    >
                      {showPassword ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                </div>
                
                {/* Forgot Password Link */}
                <div className="text-right">
                  <Link href="/forgot-password" className="text-sm text-green-600 hover:text-green-700 font-medium">
                    Forgot your password?
                  </Link>
                </div>
                
                <TurnstileWidget ref={captchaRef} onToken={setCaptchaToken} />

                <Button
                  variant={'green'}
                  type="submit"
                  className="w-full"
                  disabled={isLoading || (isCaptchaConfigured && !captchaToken)}
                >
                  {isLoading ? (
                    <span className="flex items-center justify-center">
                      <ButtonSpinner />
                      Logging in...
                    </span>
                  ) : (
                    <span className="flex font-normal items-center justify-center">
                      Login to MenuMate
                    </span>
                  )}
                </Button>

                {/* OR Divider */}
                <div className="relative mx-10">
                  <div className="absolute inset-0 flex items-center">
                    <span className="w-full border-t border-gray-300" />
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="bg-white px-2 text-gray-500">or</span>
                  </div>
                </div>
              </form>
            </CardContent>
            <CardFooter className="flex flex-col items-center space-y-4">
              <div className="flex items-center gap-2 mb-9">
                <p className="text-sm text-gray-900">
                  New to MenuMate?{' '}
                  <Link href="/signup" className="text-green-600 hover:text-green-700 font-medium">
                    <span className="inline-flex items-center">Get Started <ArrowRight className="ml-2 h-4 w-4" /></span> 
                  </Link>
                </p>
              </div>
            </CardFooter>
          </Card>

          <div ref={footerRef} className="text-center">
            <p className="text-sm text-white/80 flex items-center justify-center gap-2">
              <Shield className="w-4 h-4" />
              Trusted by caterers across India
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function LoginPage() {
  return (
    <Suspense fallback={<div>Loading...</div>}>
      <LoginForm />
    </Suspense>
  );
}